/**
 * FIM/FAM Domain Scoring Guidelines
 * 7-point scale reference for each domain
 */

export const DOMAIN_GUIDELINES = {
  // SELF CARE
  selfcare_eating: {
    title: "Eating",
    levels: {
      7: "Manages a normal diet of their choice in a reasonable period of time.",
      6: "Manages a normal diet by mouth, but may take excessive time for eating or use assistive devices.",
      5: "Takes all nourishment by mouth, may require supervision or setup assistance.",
      4: "Takes primary nourishment by mouth with minimal contact assistance for utensils or food preparation.",
      3: "Takes some nourishment by mouth with moderate assistance (e.g., cutting food, holding utensils).",
      2: "Unable to receive adequate nourishment by oral feeding alone, requires maximum assistance.",
      1: "Total assistance required, dependent on others for eating."
    }
  },
  
  selfcare_swallowing: {
    title: "Swallowing",
    levels: {
      7: "Manages a normal diet of their choice in a reasonable period of time.",
      6: "Manages a normal diet by mouth, but may take excessive time for eating or use assistive devices or strategies to clear food, e.g. multiple swallows. If needs to avoid certain food consistencies, chooses these independently.",
      5: "Takes all nourishment by mouth, may require a modified diet and has assistance with food choices to ensure that the correct food is provided, supervision may be received for cueing or coaxing e.g. reminding to slow down.",
      4: "Takes primary nourishment by mouth, may require diet modifications and has only minimal contact to control speed and amount of food intake. If has any enteral feeding, these are small volume top-ups only.",
      3: "Takes some nourishment by mouth, may require diet modifications such as pureed food. Receives moderate assistance to monitor speed and amount of food intake (e.g. every mouthful). Or has a mixture of oral and enteral feeding, but takes more than half by mouth.",
      2: "Unable to receive adequate nourishment by oral feeding alone, tube feeding provides primary nutrition, oral feeding is limited and subject receives maximum assistance.",
      1: "Unable to take anything by mouth, nutrition is provided by tube feeding."
    }
  },
  
  selfcare_grooming: {
    title: "Grooming",
    levels: {
      7: "Completes grooming tasks independently (brushing teeth, combing hair, washing face/hands, shaving).",
      6: "Performs grooming independently with assistive devices or takes extra time.",
      5: "Needs setup assistance (e.g., getting items ready) but completes tasks independently.",
      4: "Requires minimal physical assistance or supervision for grooming tasks.",
      3: "Requires moderate assistance for 25-50% of grooming tasks.",
      2: "Requires maximum assistance for 50-75% of grooming tasks.",
      1: "Total dependence, requires full assistance for all grooming."
    }
  },
  
  selfcare_bathing: {
    title: "Bathing",
    levels: {
      7: "Bathes independently, including getting in/out of tub or shower.",
      6: "Bathes independently using assistive devices or adapted techniques.",
      5: "Needs setup only (e.g., adjusting water temperature, gathering supplies).",
      4: "Requires minimal physical assistance for washing difficult-to-reach areas.",
      3: "Requires moderate assistance for washing body (not including back).",
      2: "Requires maximum assistance, performs less than 50% of bathing tasks.",
      1: "Total dependence, requires full assistance for all bathing."
    }
  },
  
  selfcare_dressing_upper: {
    title: "Dressing Upper Body",
    levels: {
      7: "Dresses upper body independently, including obtaining clothes and fasteners.",
      6: "Dresses independently using assistive devices or adapted clothing.",
      5: "Needs setup assistance (e.g., clothes laid out) but dresses independently.",
      4: "Requires minimal physical assistance with difficult items (buttons, zippers).",
      3: "Requires moderate assistance for 25-50% of upper body dressing.",
      2: "Requires maximum assistance for 50-75% of upper body dressing.",
      1: "Total dependence, requires full assistance for all upper body dressing."
    }
  },
  
  selfcare_dressing_lower: {
    title: "Dressing Lower Body",
    levels: {
      7: "Dresses lower body independently, including obtaining clothes and footwear.",
      6: "Dresses independently using assistive devices or adapted clothing/shoes.",
      5: "Needs setup assistance (e.g., clothes laid out) but dresses independently.",
      4: "Requires minimal physical assistance with difficult items (socks, shoes, fasteners).",
      3: "Requires moderate assistance for 25-50% of lower body dressing.",
      2: "Requires maximum assistance for 50-75% of lower body dressing.",
      1: "Total dependence, requires full assistance for all lower body dressing."
    }
  },
  
  selfcare_toileting: {
    title: "Toileting",
    levels: {
      7: "Uses toilet completely independently, including transfers, clothing, and hygiene.",
      6: "Uses toilet independently with assistive devices or adapted techniques.",
      5: "Needs setup assistance or supervision but manages toileting independently.",
      4: "Requires minimal physical assistance for clothing or hygiene.",
      3: "Requires moderate assistance for transfers, clothing, or hygiene.",
      2: "Requires maximum assistance, performs less than 50% of toileting tasks.",
      1: "Total dependence, requires full assistance for all toileting activities."
    }
  },
  
  // BLADDER MANAGEMENT
  bladder_level_of_assistance: {
    title: "Bladder Management - Level of Assistance",
    levels: {
      7: "Manages bladder completely independently (no accidents, manages equipment if used).",
      6: "Manages bladder independently with devices (catheter, collection device) or medications.",
      5: "Needs supervision or assistance with equipment setup, but no accidents.",
      4: "Requires minimal assistance with equipment or occasional prompting, rare accidents.",
      3: "Requires moderate assistance with bladder management, occasional accidents.",
      2: "Requires maximum assistance, frequent accidents requiring assistance.",
      1: "Total dependence, no bladder control, requires complete assistance."
    }
  },
  
  bladder_frequency_of_accidents: {
    title: "Bladder Management - Frequency of Accidents",
    levels: {
      7: "No accidents (0 per week).",
      6: "No more than 1 accident per week, manages independently.",
      5: "No more than 1 accident per week with supervision.",
      4: "No more than 2-3 accidents per week.",
      3: "Daily accidents but not hourly.",
      2: "Hourly or more frequent accidents.",
      1: "Continuous incontinence with no control."
    }
  },
  
  // BOWEL MANAGEMENT
  bowel_level_of_assistance: {
    title: "Bowel Management - Level of Assistance",
    levels: {
      7: "Manages bowels completely independently (no accidents, manages equipment if used).",
      6: "Manages bowels independently with devices, medications, or suppositories.",
      5: "Needs supervision or assistance with equipment setup, but no accidents.",
      4: "Requires minimal assistance with equipment or occasional prompting, rare accidents.",
      3: "Requires moderate assistance with bowel management, occasional accidents.",
      2: "Requires maximum assistance, frequent accidents requiring assistance.",
      1: "Total dependence, no bowel control, requires complete assistance."
    }
  },
  
  bowel_frequency_of_accidents: {
    title: "Bowel Management - Frequency of Accidents",
    levels: {
      7: "No accidents (0 per week).",
      6: "No more than 1 accident per month, manages independently.",
      5: "No more than 1 accident per month with supervision.",
      4: "No more than 2 accidents per month.",
      3: "Weekly accidents but not daily.",
      2: "Daily accidents.",
      1: "More than daily accidents with no control."
    }
  },
  
  // MOBILITY
  mobility_bed_chair_wheelchair: {
    title: "Mobility - Bed/Chair/Wheelchair Transfers",
    levels: {
      7: "Transfers independently and safely in all situations.",
      6: "Transfers independently using assistive devices or adapted techniques.",
      5: "Needs supervision or setup assistance but transfers independently.",
      4: "Requires minimal physical assistance or touching for safety during transfers.",
      3: "Requires moderate assistance, performs 50-75% of transfer independently.",
      2: "Requires maximum assistance, performs 25-50% of transfer independently.",
      1: "Total dependence, unable to participate in transfer (e.g., requires lift/hoist)."
    }
  },
  
  mobility_toilet: {
    title: "Mobility - Toilet Transfers",
    levels: {
      7: "Transfers on/off toilet independently and safely.",
      6: "Transfers independently using grab rails or assistive devices.",
      5: "Needs supervision or standby assistance but transfers independently.",
      4: "Requires minimal physical contact assistance for balance or safety.",
      3: "Requires moderate assistance, performs 50-75% of transfer independently.",
      2: "Requires maximum assistance, performs 25-50% of transfer independently.",
      1: "Total dependence, unable to participate in toilet transfer."
    }
  },
  
  mobility_tub_shower: {
    title: "Mobility - Tub/Shower Transfers",
    levels: {
      7: "Transfers in/out of tub or shower independently and safely.",
      6: "Transfers independently using grab rails, shower chair, or assistive devices.",
      5: "Needs supervision or setup assistance but transfers independently.",
      4: "Requires minimal physical contact assistance for balance or safety.",
      3: "Requires moderate assistance, performs 50-75% of transfer independently.",
      2: "Requires maximum assistance, performs 25-50% of transfer independently.",
      1: "Total dependence, unable to participate in tub/shower transfer."
    }
  },
  
  mobility_car: {
    title: "Mobility - Car Transfers",
    levels: {
      7: "Transfers in/out of car independently, including managing doors and seatbelt.",
      6: "Transfers independently with assistive devices or adapted techniques.",
      5: "Needs supervision or door/seatbelt assistance but transfers independently.",
      4: "Requires minimal physical assistance for positioning or balance.",
      3: "Requires moderate assistance, performs 50-75% of transfer independently.",
      2: "Requires maximum assistance, performs 25-50% of transfer independently.",
      1: "Total dependence, unable to participate in car transfer."
    }
  },
  
  // LOCOMOTION
  locomotion_walking: {
    title: "Locomotion - Walking",
    levels: {
      7: "Walks independently for 150+ feet without aids, normal speed and safety.",
      6: "Walks independently 150+ feet with assistive devices (cane, walker, prosthesis).",
      5: "Walks 150+ feet with supervision or standby assistance.",
      4: "Walks 150+ feet with minimal physical assistance or constant supervision.",
      3: "Walks 50-149 feet with moderate assistance.",
      2: "Walks less than 50 feet with maximum assistance or uses wheelchair for distances.",
      1: "Unable to walk, dependent on wheelchair or complete assistance for mobility."
    }
  },
  
  locomotion_wheelchair: {
    title: "Locomotion - Wheelchair",
    levels: {
      7: "Propels wheelchair independently 150+ feet, navigates obstacles, turns, and manages doors.",
      6: "Propels wheelchair independently 150+ feet on level surfaces with standard wheelchair.",
      5: "Propels wheelchair 150+ feet with supervision or setup assistance.",
      4: "Propels wheelchair 150+ feet with minimal assistance for obstacles or slopes.",
      3: "Propels wheelchair 50-149 feet with moderate assistance.",
      2: "Propels wheelchair less than 50 feet or requires maximum assistance.",
      1: "Unable to propel wheelchair, completely dependent on others for wheelchair mobility."
    }
  },
  
  locomotion_stairs: {
    title: "Locomotion - Stairs",
    levels: {
      7: "Climbs 12-15 stairs independently and safely without aids.",
      6: "Climbs 12-15 stairs independently using assistive devices or railings.",
      5: "Climbs 12-15 stairs with supervision or standby assistance.",
      4: "Climbs 12-15 stairs with minimal physical assistance or contact guarding.",
      3: "Climbs 4-11 stairs with moderate assistance.",
      2: "Climbs 1-3 stairs with maximum assistance.",
      1: "Unable to climb stairs even with assistance."
    }
  },
  
  mobility_community: {
    title: "Mobility - Community Access",
    levels: {
      7: "Accesses community independently and safely (shops, appointments, social activities).",
      6: "Accesses community independently using assistive devices or adapted transport.",
      5: "Needs supervision or setup assistance but accesses community independently.",
      4: "Requires minimal physical assistance or accompaniment for safety in community.",
      3: "Requires moderate assistance for community access, limited independent mobility.",
      2: "Requires maximum assistance, rarely accesses community independently.",
      1: "Total dependence, cannot access community without full assistance."
    }
  },
  
  // COMMUNICATION
  communication_comprehension: {
    title: "Communication - Comprehension",
    levels: {
      7: "Comprehends complex spoken language without difficulty.",
      6: "Comprehends spoken language with minimal difficulty, may need repetition occasionally.",
      5: "Comprehends simple spoken language consistently, complex language with extra time or repetition.",
      4: "Comprehends simple language with minimal cues or prompting 75% of the time.",
      3: "Comprehends simple language with moderate cues or prompting 50% of the time.",
      2: "Comprehends only basic language with maximum cues, less than 25% comprehension.",
      1: "Unable to comprehend spoken language meaningfully."
    }
  },
  
  communication_expression: {
    title: "Communication - Expression",
    levels: {
      7: "Expresses complex ideas clearly and fluently in all situations.",
      6: "Expresses complex ideas with minimal difficulty, may have mild word-finding issues.",
      5: "Expresses basic needs and ideas consistently, complex expression with extra time.",
      4: "Expresses basic needs intelligibly 75% of the time with minimal prompting.",
      3: "Expresses basic needs 50% of the time with moderate prompting or assistance.",
      2: "Expresses basic needs less than 25% of the time, requires maximum assistance.",
      1: "Unable to express needs or ideas meaningfully."
    }
  },
  
  communication_reading: {
    title: "Communication - Reading",
    levels: {
      7: "Reads and comprehends complex written material without difficulty.",
      6: "Reads and comprehends written material with minimal difficulty, may need extra time.",
      5: "Reads and comprehends simple written material consistently.",
      4: "Reads and comprehends simple material with minimal assistance 75% of the time.",
      3: "Reads and comprehends simple material with moderate assistance 50% of the time.",
      2: "Reads minimally with maximum assistance, less than 25% comprehension.",
      1: "Unable to read or comprehend written material."
    }
  },
  
  communication_writing: {
    title: "Communication - Writing",
    levels: {
      7: "Writes complex material legibly and coherently without difficulty.",
      6: "Writes coherently with minimal difficulty, may have minor errors or need assistive devices.",
      5: "Writes simple sentences or messages consistently, may need extra time.",
      4: "Writes simple messages with minimal assistance 75% of the time.",
      3: "Writes simple messages with moderate assistance 50% of the time.",
      2: "Writes minimally with maximum assistance, less than 25% success.",
      1: "Unable to write meaningfully."
    }
  },
  
  communication_speech_intelligibility: {
    title: "Communication - Speech Intelligibility",
    levels: {
      7: "Speech is always clear and easily understood by everyone.",
      6: "Speech is usually clear, occasionally needs repetition for unfamiliar listeners.",
      5: "Speech is intelligible to familiar listeners, unfamiliar listeners may need repetition.",
      4: "Speech is 75% intelligible, requires some context or repetition.",
      3: "Speech is 50% intelligible, requires moderate repetition or interpretation.",
      2: "Speech is less than 25% intelligible, requires maximum effort to understand.",
      1: "Speech is unintelligible or no functional speech."
    }
  },
  
  // PSYCHOSOCIAL
  psychosocial_social_interaction: {
    title: "Psychosocial - Social Interaction",
    levels: {
      7: "Interacts appropriately in all social situations, initiates and maintains relationships.",
      6: "Interacts appropriately with minimal difficulty, occasional social misjudgments.",
      5: "Interacts appropriately with supervision or prompting in complex social situations.",
      4: "Requires minimal assistance to interact appropriately, some social awkwardness.",
      3: "Requires moderate assistance or prompting for appropriate social interaction.",
      2: "Requires maximum assistance, limited appropriate social interaction.",
      1: "Unable to interact appropriately, may be withdrawn or consistently inappropriate."
    }
  },
  
  psychosocial_emotional_status: {
    title: "Psychosocial - Emotional Status",
    levels: {
      7: "Emotional responses are appropriate and well-modulated in all situations.",
      6: "Emotional responses usually appropriate, occasional mild dysregulation.",
      5: "Emotional responses generally appropriate with supervision or environmental modifications.",
      4: "Requires minimal support to maintain appropriate emotional responses.",
      3: "Requires moderate support, noticeable emotional dysregulation affecting function.",
      2: "Requires maximum support, significant emotional dysregulation requiring intervention.",
      1: "Severe emotional dysregulation, unable to participate in activities."
    }
  },
  
  psychosocial_adjustment_to_limitations: {
    title: "Psychosocial - Adjustment to Limitations",
    levels: {
      7: "Fully accepts limitations, actively participates in rehabilitation and adaptation.",
      6: "Generally accepts limitations, occasional frustration but maintains progress.",
      5: "Accepts limitations with support, requires encouragement to engage with rehabilitation.",
      4: "Requires minimal support to accept limitations and engage with treatment.",
      3: "Requires moderate support, noticeable difficulty accepting limitations affecting progress.",
      2: "Requires maximum support, significant denial or resistance to treatment.",
      1: "Complete rejection of limitations, unable to engage with rehabilitation process."
    }
  },
  
  psychosocial_leisure_activities: {
    title: "Psychosocial - Leisure Activities",
    levels: {
      7: "Independently engages in varied leisure activities and hobbies.",
      6: "Engages in leisure activities with minimal adaptation or assistive devices.",
      5: "Engages in leisure activities with setup assistance or supervision.",
      4: "Requires minimal assistance to engage in simplified leisure activities.",
      3: "Requires moderate assistance to participate in leisure activities.",
      2: "Requires maximum assistance, limited participation in leisure activities.",
      1: "Unable to engage in leisure activities even with full support."
    }
  },
  
  // COGNITION
  cognition_problem_solving: {
    title: "Cognition - Problem Solving",
    levels: {
      7: "Solves complex problems independently and appropriately in all situations.",
      6: "Solves routine problems independently, may need extra time for complex situations.",
      5: "Solves routine problems with supervision, can address simple issues independently.",
      4: "Requires minimal assistance to solve routine problems 75% of the time.",
      3: "Requires moderate assistance to solve routine problems 50% of the time.",
      2: "Requires maximum assistance, recognizes problems less than 25% of the time.",
      1: "Unable to recognize or solve problems, completely dependent on others."
    }
  },
  
  cognition_memory: {
    title: "Cognition - Memory",
    levels: {
      7: "Remembers daily routines, tasks, and information without difficulty.",
      6: "Remembers with minimal difficulty, may use memory aids or strategies successfully.",
      5: "Remembers routine information with supervision or structured memory aids.",
      4: "Requires minimal prompting or cues to remember 75% of the time.",
      3: "Requires moderate prompting or cues to remember 50% of the time.",
      2: "Requires maximum cues and reminders, recalls less than 25% independently.",
      1: "Unable to retain new information, no functional memory."
    }
  },
  
  cognition_orientation: {
    title: "Cognition - Orientation",
    levels: {
      7: "Fully oriented to person, place, time, and situation at all times.",
      6: "Oriented with minimal difficulty, may occasionally need reminders about date/schedule.",
      5: "Generally oriented with supervision, may be confused in new or complex situations.",
      4: "Requires minimal prompting to maintain orientation 75% of the time.",
      3: "Requires moderate prompting to maintain orientation 50% of the time.",
      2: "Requires maximum prompting, oriented less than 25% of the time.",
      1: "Consistently disoriented to person, place, time, and situation."
    }
  },
  
  cognition_concentration: {
    title: "Cognition - Concentration",
    levels: {
      7: "Maintains concentration on tasks without difficulty, even with distractions.",
      6: "Maintains concentration with minimal difficulty, may need quiet environment.",
      5: "Maintains concentration on simple tasks with supervision or reduced distractions.",
      4: "Requires minimal redirection to maintain concentration 75% of the time.",
      3: "Requires moderate redirection to maintain concentration 50% of the time.",
      2: "Requires maximum redirection, concentrates less than 25% of the time.",
      1: "Unable to maintain concentration on any task, even briefly."
    }
  },
  
  cognition_safety_awareness: {
    title: "Cognition - Safety Awareness",
    levels: {
      7: "Recognizes and responds appropriately to all safety hazards.",
      6: "Recognizes safety hazards with minimal difficulty, occasionally needs reminders.",
      5: "Recognizes obvious safety hazards with supervision, needs prompting for subtle risks.",
      4: "Requires minimal supervision to maintain safety 75% of the time.",
      3: "Requires moderate supervision to maintain safety 50% of the time.",
      2: "Requires maximum supervision, recognizes safety hazards less than 25% of the time.",
      1: "No awareness of safety hazards, requires constant supervision to prevent harm."
    }
  },
  
  // ACTIVITIES OF DAILY LIVING
  adl_meal_preparation: {
    title: "ADL - Meal Preparation",
    levels: {
      7: "Plans and prepares all meals independently, including shopping and cleaning.",
      6: "Prepares meals independently with assistive devices or adapted techniques.",
      5: "Prepares simple meals independently, needs supervision for complex cooking.",
      4: "Requires minimal assistance with meal preparation (e.g., reaching items, setup).",
      3: "Requires moderate assistance, can complete 50% of meal preparation tasks.",
      2: "Requires maximum assistance, completes less than 25% of meal preparation.",
      1: "Total dependence, unable to participate in meal preparation."
    }
  },
  
  adl_laundry: {
    title: "ADL - Laundry",
    levels: {
      7: "Completes all laundry tasks independently (sorting, washing, drying, folding, putting away).",
      6: "Completes laundry independently with assistive devices or adapted techniques.",
      5: "Completes laundry with supervision or setup assistance.",
      4: "Requires minimal physical assistance for 25% of laundry tasks.",
      3: "Requires moderate assistance for 50% of laundry tasks.",
      2: "Requires maximum assistance for 75% of laundry tasks.",
      1: "Total dependence, unable to participate in laundry tasks."
    }
  },
  
  adl_housework: {
    title: "ADL - Housework",
    levels: {
      7: "Completes all housework tasks independently (cleaning, vacuuming, tidying).",
      6: "Completes housework independently with assistive devices or adapted techniques.",
      5: "Completes light housework independently, needs supervision for heavier tasks.",
      4: "Requires minimal assistance for 25% of housework tasks.",
      3: "Requires moderate assistance for 50% of housework tasks.",
      2: "Requires maximum assistance for 75% of housework tasks.",
      1: "Total dependence, unable to participate in housework."
    }
  },
  
  adl_work_edu: {
    title: "ADL - Work/Education",
    levels: {
      7: "Fully independent in work or educational setting, meets all demands.",
      6: "Independent in work/education with minor adaptations or assistive technology.",
      5: "Participates in work/education with supervision or workplace accommodations.",
      4: "Requires minimal support to participate in modified work/educational activities.",
      3: "Requires moderate support, participates in 50% of work/educational activities.",
      2: "Requires maximum support, limited participation in work/education.",
      1: "Unable to participate in work or educational activities."
    }
  },
  
  adl_finances: {
    title: "ADL - Financial Management",
    levels: {
      7: "Manages all finances independently (budgeting, bills, banking, taxes).",
      6: "Manages finances independently with minor assistance (e.g., online banking tools).",
      5: "Manages routine finances with supervision, needs help with complex financial decisions.",
      4: "Requires minimal assistance to manage basic finances (e.g., paying bills).",
      3: "Requires moderate assistance, understands basic transactions with support.",
      2: "Requires maximum assistance, minimal participation in financial management.",
      1: "Total dependence, unable to participate in financial management."
    }
  },
  
  adl_shopping: {
    title: "ADL - Shopping",
    levels: {
      7: "Shops independently for all needs (groceries, clothing, household items).",
      6: "Shops independently with assistive devices or adapted techniques (e.g., online shopping).",
      5: "Shops independently for simple items, needs supervision for complex shopping trips.",
      4: "Requires minimal assistance (e.g., transportation, list-making, carrying items).",
      3: "Requires moderate assistance, can select items but needs support with planning/execution.",
      2: "Requires maximum assistance, minimal participation in shopping process.",
      1: "Total dependence, unable to participate in shopping activities."
    }
  }
};

/**
 * Get guidelines for a specific domain
 * @param {string} domainKey - The domain key (e.g., 'selfcare_eating')
 * @returns {object|null} Guidelines object or null if not found
 */
export const getDomainGuidelines = (domainKey) => {
  return DOMAIN_GUIDELINES[domainKey] || null;
};

